import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { News } from "@/types";
import NewsService from "@/services/NewsService";
import { NewsType, useCurrentLocation } from "@/stores/unified-news-store";

interface UseOptimizedNewsOptions {
  type: NewsType;
  categorySlug?: string;
  locationSlug?: string | null;
  sortBy?: "latest" | "popular" | "views" | "trending" | "comments";
  limit?: number;
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
}

interface UseOptimizedNewsReturn {
  data: News[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  isStale: boolean;
  isFetching: boolean;
}

/**
 * Optimized hook for fetching news data with TanStack Query
 * Provides smart caching, background updates, and performance optimization
 */
export const useOptimizedNews = ({
  type,
  categorySlug,
  locationSlug,
  sortBy = "latest",
  limit = 20,
  enabled = true,
  staleTime = 2 * 60 * 1000, // 2 minutes (reduced from 5 minutes)
  cacheTime = 5 * 60 * 1000, // 5 minutes (reduced from 10 minutes)
}: UseOptimizedNewsOptions): UseOptimizedNewsReturn => {
  const currentLocation = useCurrentLocation();

  // Use current location if no specific location provided
  const effectiveLocationSlug = locationSlug ?? currentLocation?.slug;

  // Create unique query key
  const queryKey = useMemo(
    () => ["news", type, categorySlug, effectiveLocationSlug, sortBy, limit],
    [type, categorySlug, effectiveLocationSlug, sortBy, limit],
  );

  // Query function
  const queryFn = async (): Promise<News[]> => {
    try {
      let response;

      // Route to appropriate service based on category or type
      if (categorySlug) {
        response = await NewsService.fetchNewsByCategory(
          categorySlug,
          1,
          limit,
          effectiveLocationSlug || undefined,
          sortBy,
        );
      } else {
        switch (type) {
          case "latest":
            response = await NewsService.fetchLatestNews(
              1,
              limit,
              effectiveLocationSlug || undefined,
            );
            break;
          case "popular":
            response = await NewsService.fetchPopularNews(
              1,
              limit,
              effectiveLocationSlug || undefined,
            );
            break;
          case "mostsee":
            response = await NewsService.fetchMostSeeNews(
              1,
              limit,
              effectiveLocationSlug || undefined,
            );
            break;
          case "celebrity":
            response = await NewsService.fetchCelebrityNews(
              1,
              limit,
              effectiveLocationSlug || undefined,
            );
            break;
          case "viral":
            response = await NewsService.fetchViralNews(
              1,
              limit,
              effectiveLocationSlug || undefined,
            );
            break;
          case "hot":
            response = await NewsService.fetchHotNews(
              1,
              limit,
              effectiveLocationSlug || undefined,
            );
            break;
          default:
            throw new Error(`Unsupported news type: ${type}`);
        }
      }

      return response?.data || [];
    } catch (error) {
      console.error(`Error fetching ${type} news:`, error);
      throw error;
    }
  };

  // Use TanStack Query
  const {
    data = [],
    isLoading,
    isError,
    error,
    refetch,
    isStale,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime,
    gcTime: cacheTime,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: false, // Disable refetching on mount if data is fresh
    retry: 2, // Reduced from 3
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000), // Reduced max delay
  });

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isStale,
    isFetching,
  };
};

/**
 * Hook for fetching homepage data (multiple news types at once)
 */
export const useOptimizedHomepageNews = (locationSlug?: string | null) => {
  const currentLocation = useCurrentLocation();
  const effectiveLocationSlug = locationSlug ?? currentLocation?.slug;

  const queryKey = ["homepage", effectiveLocationSlug];

  const queryFn = async () => {
    try {
      const [latest, popular, mostsee, celebrity, viral, hot] =
        await Promise.all([
          NewsService.fetchLatestNews(1, 8, effectiveLocationSlug || undefined),
          NewsService.fetchPopularNews(
            1,
            10,
            effectiveLocationSlug || undefined,
          ),
          NewsService.fetchMostSeeNews(
            1,
            8,
            effectiveLocationSlug || undefined,
          ),
          NewsService.fetchCelebrityNews(
            1,
            4,
            effectiveLocationSlug || undefined,
          ),
          NewsService.fetchViralNews(1, 8, effectiveLocationSlug || undefined),
          NewsService.fetchHotNews(1, 8, effectiveLocationSlug || undefined),
        ]);

      return {
        latest: latest?.data || [],
        popular: popular?.data || [],
        mostsee: mostsee?.data || [],
        celebrity: celebrity?.data || [],
        viral: viral?.data || [],
        hot: hot?.data || [],
      };
    } catch (error) {
      console.error("Error fetching homepage data:", error);
      throw error;
    }
  };

  return useQuery({
    queryKey,
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: false, // Disable refetching on mount if data is fresh
    retry: 2, // Reduced from 3
  });
};

/**
 * Convenience hooks for specific news types
 */
export const useLatestNews = (
  options?: Omit<UseOptimizedNewsOptions, "type">,
) => useOptimizedNews({ type: "latest", ...options });

export const usePopularNews = (
  options?: Omit<UseOptimizedNewsOptions, "type">,
) => useOptimizedNews({ type: "popular", ...options });

export const useMostSeeNews = (
  options?: Omit<UseOptimizedNewsOptions, "type">,
) => useOptimizedNews({ type: "mostsee", ...options });

export const useCelebrityNews = (
  options?: Omit<UseOptimizedNewsOptions, "type">,
) => useOptimizedNews({ type: "celebrity", ...options });

export const useViralNews = (options?: Omit<UseOptimizedNewsOptions, "type">) =>
  useOptimizedNews({ type: "viral", ...options });

export const useHotNews = (options?: Omit<UseOptimizedNewsOptions, "type">) =>
  useOptimizedNews({ type: "hot", ...options });

/**
 * Hook for invalidating news queries (useful for cache management)
 */
export const useInvalidateNews = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: ["news"] }),
    invalidateType: (type: NewsType) =>
      queryClient.invalidateQueries({ queryKey: ["news", type] }),
    invalidateLocation: (locationSlug: string) =>
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes("news") &&
          query.queryKey.includes(locationSlug),
      }),
    invalidateHomepage: () =>
      queryClient.invalidateQueries({ queryKey: ["homepage"] }),
  };
};

/**
 * Hook for prefetching news data
 */
export const usePrefetchNews = () => {
  const queryClient = useQueryClient();

  return {
    prefetchType: async (type: NewsType, options?: UseOptimizedNewsOptions) => {
      const queryKey = [
        "news",
        type,
        options?.categorySlug,
        options?.locationSlug,
        options?.sortBy || "latest",
        options?.limit || 20,
      ];

      await queryClient.prefetchQuery({
        queryKey,
        queryFn: async () => {
          // Use the same logic as useOptimizedNews
          // This could be extracted to a shared function
          return [];
        },
        staleTime: options?.staleTime || 5 * 60 * 1000,
      });
    },
  };
};

export default useOptimizedNews;
